import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { pricingPlans } from '../../data/lifeChangingPlans.js';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Checkbox } from '../../components/ui/checkbox';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '../../components/ui/form';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Check, Loader2 } from 'lucide-react';

import { useToast } from '../../components/ui/use-toast';
import limitedOfferImg from '../../assets/images/life-journey/limited-offer.gif';
import { getUTMForSubmit } from "../../utils/utm";

const baseSchema = z.object({
    name: z.string().min(2, { message: "Name must be at least 2 characters." }),
    email: z.string().email({ message: "Please enter a valid email address." }),
    whatsapp: z.string().min(10, { message: "Please enter a valid WhatsApp number." }),
    reportLanguage: z.string(),
    consultationDate: z.string().optional(),
    consultationTime: z.string().optional(),
    problemType: z.string().optional(),
    questionOne: z.string().optional(),
    questionTwo: z.string().optional(),
    dob: z.string({ required_error: "Please enter your date of birth." }).min(1, "Please enter your date of birth."),
    partnerDob: z.string().optional(),
    astroConsultation: z.boolean().optional(),
    expressDelivery: z.boolean().optional(),
    terms: z.boolean().refine(data => data === true, {
        message: "You must accept the terms and conditions.",
    }),
});

// Extend window object for Razorpay in JS
// @ts-ignore
window.Razorpay = window.Razorpay || undefined;

const LifeChangingReportForm = () => {
    const { planId } = useParams();
    const navigate = useNavigate();
    const { toast } = useToast();
    const [selectedPlan, setSelectedPlan] = useState(undefined);
    const [finalAmount, setFinalAmount] = useState(0);
    const [isScriptLoaded, setIsScriptLoaded] = useState(false);
    const [isProcessingPayment, setIsProcessingPayment] = useState(false);
    const utm = getUTMForSubmit();

    useEffect(() => {
        const script = document.createElement('script');
        script.src = 'https://checkout.razorpay.com/v1/checkout.js';
        script.async = true;
        script.onload = () => setIsScriptLoaded(true);
        document.body.appendChild(script);

        return () => {
            if (document.body.contains(script)) {
                document.body.removeChild(script);
            }
        };
    }, []);

    const finalSchema = useMemo(() => {
        let schema = baseSchema;

        if (planId === 'life-changing-report-ask-astrologer') {
            schema = schema.extend({
                questionOne: z.string().min(1, "Please enter your first question."),
                questionTwo: z.string().min(1, "Please enter your second question."),
            });
        } else if (planId === 'life-changing-report-consultation') {
            schema = schema.extend({
                consultationDate: z.string().min(1, "Please select a consultation date."),
                consultationTime: z.string().min(1, "Please select a consultation time."),
                problemType: z.string().min(1, "Please select a problem type."),
            });
        }

        return schema.superRefine((data, ctx) => {
            if (planId === 'life-changing-report' && data.astroConsultation) {
                
                if (data.consultationDate) {
                    // Parse the date string to avoid timezone issues
                    const parts = data.consultationDate.split('-');
                    const selectedDate = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));

                    const today = new Date();
                    today.setHours(0, 0, 0, 0);

                    const minDate = new Date(today);
                    minDate.setDate(minDate.getDate() + 2); // Set minimum date to today + 2 days in IST

                    if (selectedDate < minDate) {
                        ctx.addIssue({
                            path: ['consultationDate'],
                            message: 'Consultation date must be at least 2 days from today.',
                            code: 'custom',
                        });
                    }
                }                

                if (!data.consultationTime) ctx.addIssue({ path: ['consultationTime'], message: 'Time is required for consultation.', code: 'custom' });
                if (!data.problemType) ctx.addIssue({ path: ['problemType'], message: 'Problem type is required for consultation.', code: 'custom' });
            }

            if (planId === 'life-changing-report-consultation') {
                if (data.consultationDate) {
                    const parts = data.consultationDate.split('-');
                    const selectedDate = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));

                    const today = new Date();
                    today.setHours(0, 0, 0, 0);

                    const minDate = new Date(today);
                    minDate.setDate(minDate.getDate() + 2);

                    if (selectedDate < minDate) {
                        ctx.addIssue({
                            path: ['consultationDate'],
                            message: 'Consultation date must be at least 2 days from today.',
                            code: 'custom',
                        });
                    }
                }
            }            

            if (data.problemType === 'couple' && !data.partnerDob) {
                ctx.addIssue({ code: 'custom', message: "Partner's date of birth is required.", path: ["partnerDob"] });
            }
        });
    }, [planId]);

    const form = useForm({
        resolver: zodResolver(finalSchema),
        defaultValues: {
            name: "", email: "", whatsapp: "", reportLanguage: "english",
            consultationDate: "", consultationTime: "", problemType: "", questionOne: "",
            questionTwo: "", dob: "", partnerDob: "", astroConsultation: false, expressDelivery: false, terms: false,
        },
    });

    const problemType = form.watch("problemType");
    const watchAstroConsultation = form.watch("astroConsultation");
    const watchExpressDelivery = form.watch("expressDelivery");

    useEffect(() => {
        const plan = pricingPlans.find(p => p.id === planId);
        
        if (plan) {
            setSelectedPlan(plan);
            let currentAmount = parseInt(plan.priceFinal.replace('₹', ''));
            if (plan.id === 'life-changing-report') {
                if (watchAstroConsultation) currentAmount += plan.astroConsultationPrice || 0;
                if (watchExpressDelivery) currentAmount += plan.expressDeliveryPrice || 0;
            }
            setFinalAmount(currentAmount);
        } else {
            navigate('/life-changing-report');
        }
    }, [planId, navigate, watchAstroConsultation, watchExpressDelivery]);

    const createRazorpayOrder = async (amount, formData) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URLS_HTTP}api/life-journey-report/create-order`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ amount, formData }),
            });
            if (!response.ok) throw new Error('Network response was not ok');
            const data = await response.json();
            return data;
        } catch (error) {
            console.error("Error creating Razorpay order:", error);
            toast({
                variant: "destructive",
                title: "Server Error",
                description: "Could not create an order. Please try again later.",
            });
            return null;
        } finally {
            setIsProcessingPayment(false);
        }
    };

    async function onSubmit(values) {
        if (!selectedPlan || !isScriptLoaded) {
            toast({
                variant: "destructive",
                title: "Payment gateway is not ready yet.",
                description: "Please wait a moment and try again.",
            });
            return;
        }
        toast({ title: "Processing your request...", description: "Please wait." });
        const payload = { ...values, planName: selectedPlan?.title || '' };
        const orderDetails = await createRazorpayOrder(finalAmount, payload);
        if (!orderDetails || !orderDetails.orderId) return;

        const razorpayKeyId = process.env.REACT_APP_RAZORPAY_SANDBOX === 'true'
            ? process.env.REACT_APP_RAZORPAY_NEW_TEST_KEY_ID
            : process.env.REACT_APP_RAZORPAY_KEY_ID;

        const options = {
            key: razorpayKeyId,
            amount: finalAmount * 100,
            currency: "INR",
            name: "Life Changing Report",
            description: selectedPlan.title,
            image: "/favicon.ico",
            order_id: orderDetails.orderId,
            handler: async function (response) {

                setIsProcessingPayment(true);

                try {
                    // This will run in the background while the loader is showing
                    await fetch(`${process.env.REACT_APP_API_URLS_HTTP}api/life-journey-report/send-success-email`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ 
                            formData: values, 
                            razorpayResponse: response, 
                            planName: selectedPlan.title,
                            dbOrderId: orderDetails.dbOrderId, 
                            utm: utm 
                        }),
                    });
                } catch (error) {
                    setIsProcessingPayment(false);
                    console.error("Failed to send success email:", error);
                    // Optional: You could show a toast here, but still navigate them to the success page
                } finally {
                    setIsProcessingPayment(false);
                    // 3. Navigate after async operations are complete
                    const clientOrderId = `CN-${Date.now()}`;
                    navigate('/order-confirmed', { state: { orderId: clientOrderId, plan: selectedPlan, finalAmount: finalAmount } });
                }
            },
            prefill: { name: values.name, email: values.email, contact: values.whatsapp },
            theme: { color: "#391414" },
        };
        console.log('options',options);
        const rzp = new window.Razorpay(options);
        rzp.on('payment.failed', function (response) {
            // Optional: Ensure loader is off if they close the modal and fail
            setIsProcessingPayment(false);
            toast({ variant: "destructive", title: "Payment Failed", description: response.error.description || "Please try again." });
        });
        rzp.open();
    }

    // Reusable JSX for Consultation Fields
    const consultationFields = (
        <>
            <FormField control={form.control} name="consultationDate" render={({ field }) => (<FormItem><FormLabel>Consultation Date *</FormLabel><FormControl><Input type="date" {...field} /></FormControl><FormMessage /></FormItem>)} />
            <FormField control={form.control} name="consultationTime" render={({ field }) => (<FormItem><FormLabel>Consultation Time *</FormLabel><Select onValueChange={field.onChange} defaultValue={field.value}><FormControl><SelectTrigger><SelectValue placeholder="--Select Time--" /></SelectTrigger></FormControl><SelectContent><SelectItem value="10AM-12PM">10AM - 12PM</SelectItem><SelectItem value="12PM-2PM">12PM - 2PM</SelectItem><SelectItem value="2PM-4PM">2PM - 4PM</SelectItem><SelectItem value="4PM-6PM">4PM - 6PM</SelectItem></SelectContent></Select><FormMessage /></FormItem>)} />
            <FormField control={form.control} name="problemType" render={({ field }) => (<FormItem className="md:col-span-2"><FormLabel>Problem Type *</FormLabel><Select onValueChange={field.onChange} defaultValue={field.value}><FormControl><SelectTrigger><SelectValue placeholder="Select a problem type" /></SelectTrigger></FormControl><SelectContent><SelectItem value="career">Career</SelectItem><SelectItem value="health">Health</SelectItem><SelectItem value="finance">Finance</SelectItem><SelectItem value="couple">Couple Problems</SelectItem><SelectItem value="other">Other</SelectItem></SelectContent></Select><FormMessage /></FormItem>)} />
        </>
    );

    // Reusable JSX for Partner Details Fields
    const partnerDetailsFields = (
        <div className="space-y-4 pt-4 border-t border-cosmic-gold/20">
            <h3 className="font-semibold text-lg">Partner's Birth Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormField control={form.control} name="partnerDob" render={({ field }) => (<FormItem><FormLabel>Partner's Date of Birth *</FormLabel><FormControl><Input type="date" {...field} /></FormControl><FormMessage /></FormItem>)} />
            </div>
        </div>
    );

    if (!selectedPlan) return <div className="min-h-screen flex items-center justify-center">Loading...</div>;

    return (
        <div className="min-h-screen bg-background_red text-foreground py-12">

            {isProcessingPayment && (
                <div className="fixed inset-0 bg-background/80 backdrop-blur-sm flex flex-col items-center justify-center z-50">
                    <Loader2 className="w-12 h-12 text-cosmic-gold animate-spin mb-4" />
                    <h2 className="text-2xl font-bold text-foreground">Finalizing your order...</h2>
                    <p className="text-muted-foreground">Please do not refresh or close the page.</p>
                </div>
            )}

            <div className="container mx-auto px-6">
                <h1 className="text-3xl lg:text-4xl font-bold text-center mb-4 text-cosmic-gold">
                    Book Your Life Changing Report
                </h1>
                <div className="grid lg:grid-cols-3 gap-8 mt-12">
                    <div className="lg:col-span-1">
                        <Card className="p-6 border-cosmic-gold/20 bg-card/50 backdrop-blur-sm">
                            <img loading="lazy" src={selectedPlan.image} alt={selectedPlan.title} className="rounded-lg mb-4 w-full" />
                            <h2 className="text-2xl font-bold text-foreground">{selectedPlan.title}</h2>
                            <div className="my-4">
                                <span className="text-xl text-muted-foreground line-through">{selectedPlan.priceOriginal}</span>
                                <span className="text-4xl font-extrabold text-cosmic-gold ml-2">{selectedPlan.priceFinal}</span>
                            </div>
                            <h3 className="font-semibold text-lg mb-2">What's Included:</h3>
                            <ul className="space-y-3 text-left">
                                {selectedPlan.features.map((feature, i) => (
                                    <li key={i} className="flex items-start gap-3">
                                        <Check className="w-5 h-5 text-cosmic-gold flex-shrink-0 mt-1" />
                                        <span className="text-sm text-muted-foreground">{feature}</span>
                                    </li>
                                ))}
                            </ul>
                            <p className="text-sm text-muted-foreground mt-6">{selectedPlan.description}</p>
                        </Card>
                    </div>
                    <div className="lg:col-span-2">
                        <Card className="p-8 border-cosmic-gold/30 bg-card/70 backdrop-blur-sm">
                            <Form {...form}>
                                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <FormField control={form.control} name="name" render={({ field }) => (<FormItem><FormLabel>Name *</FormLabel><FormControl><Input placeholder="Enter your name" {...field} /></FormControl><FormMessage /></FormItem>)} />
                                        <FormField control={form.control} name="email" render={({ field }) => (<FormItem><FormLabel>Email *</FormLabel><FormControl><Input placeholder="Enter your email" {...field} /></FormControl><FormMessage /></FormItem>)} />
                                        <FormField control={form.control} name="whatsapp" render={({ field }) => (<FormItem><FormLabel>WhatsApp Number *</FormLabel><FormControl><Input placeholder="Enter your WhatsApp number" {...field} /></FormControl><FormMessage /></FormItem>)} />
                                        <FormField control={form.control} name="dob" render={({ field }) => (<FormItem><FormLabel>Date of Birth *</FormLabel><FormControl><Input type="date" {...field} /></FormControl><FormMessage /></FormItem>)} />
                                        <FormField control={form.control} name="reportLanguage" render={({ field }) => (<FormItem><FormLabel>Report Language *</FormLabel><Select onValueChange={field.onChange} defaultValue={field.value}><FormControl><SelectTrigger><SelectValue /></SelectTrigger></FormControl><SelectContent><SelectItem value="english">English</SelectItem><SelectItem value="hindi">Hindi</SelectItem></SelectContent></Select><FormMessage /></FormItem>)} />
                                        {planId === 'life-changing-report-consultation' && consultationFields}
                                        {planId === 'life-changing-report-ask-astrologer' && (
                                            <>
                                                <FormField control={form.control} name="questionOne" render={({ field }) => (<FormItem className="md:col-span-2"><FormLabel>Question One *</FormLabel><FormControl><Input placeholder="Enter your first question" {...field} /></FormControl><FormMessage /></FormItem>)} />
                                                <FormField control={form.control} name="questionTwo" render={({ field }) => (<FormItem className="md:col-span-2"><FormLabel>Question Two *</FormLabel><FormControl><Input placeholder="Enter your second question" {...field} /></FormControl><FormMessage /></FormItem>)} />
                                            </>
                                        )}
                                    </div>
                                    {planId === 'life-changing-report' && (
                                        <div className="space-y-4 pt-4 border-t border-cosmic-gold/20">
                                            <div className="p-4 rounded-lg border border-cosmic-gold/30 bg-background/50">
                                                <div className="flex justify-between items-center">
                                                    <FormField control={form.control} name="astroConsultation" render={({ field }) => (<FormItem className="flex flex-row items-center space-x-3 space-y-0"><FormControl><Checkbox checked={field.value} onCheckedChange={field.onChange} /></FormControl><FormLabel className="font-semibold text-base">Astro Consultation <span className='line-through opacity-80'>₹5100</span> (+₹{selectedPlan.astroConsultationPrice})<p className="text-sm font-normal text-muted-foreground">Need more clarity? Talk to an astrologer one-on-one.</p></FormLabel></FormItem>)} />
                                                    <img loading="lazy" src={limitedOfferImg} alt="Limited Time Offer" className="w-24 flex-shrink-0" />
                                                </div>
                                                {watchAstroConsultation && (
                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4 pt-4 border-t border-cosmic-gold/20">
                                                        {consultationFields}
                                                    </div>
                                                )}
                                            </div>
                                            <div className="p-4 rounded-lg border border-cosmic-gold/30 bg-background/50">
                                                <FormField control={form.control} name="expressDelivery" render={({ field }) => (<FormItem className="flex flex-row items-center space-x-3 space-y-0"><FormControl><Checkbox checked={field.value} onCheckedChange={field.onChange} /></FormControl><FormLabel className="font-semibold text-base">Express Delivery (+₹{selectedPlan.expressDeliveryPrice})<p className="text-sm font-normal text-muted-foreground">Get priority delivery of report within 24 hours.</p></FormLabel></FormItem>)} />
                                            </div>
                                        </div>
                                    )}
                                    
                                    {problemType === 'couple' && partnerDetailsFields}
                                    <FormField control={form.control} name="terms" render={({ field }) => (<FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md pt-4"><FormControl><Checkbox checked={field.value} onCheckedChange={field.onChange} /></FormControl><div className="space-y-1 leading-none"><FormLabel>I agree to the <a href="/terms-of-use" className="underline text-cosmic-gold">Terms and Conditions</a></FormLabel><FormMessage /></div></FormItem>)} />
                                    <div className="flex justify-between items-center pt-6 border-t border-cosmic-gold/20">
                                        <div>
                                            <span className="text-lg font-semibold">Final Amount:</span>
                                            <span className="text-3xl font-bold text-cosmic-gold ml-2">₹{finalAmount}</span>
                                            <span className="text-lg text-muted-foreground line-through ml-2">{selectedPlan.priceOriginal}</span>
                                        </div>
                                        <Button variant="cosmic" size="lg" type="submit">Pay with Razorpay</Button>
                                    </div>
                                </form>
                            </Form>
                        </Card>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LifeChangingReportForm;